import { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { DialogCloseButton } from "../DialogCloseButton";
import SearchIcon from "@mui/icons-material/Search";
import { useRegistry } from "../../context/RegistryContext";
import {
  useAddCategory,
  useAddExpenseType,
  useAddGroup,
  useAddInvoice,
  useAddPayee,
  useAddSource,
} from "../../api/lists";
import { EntityFormDialog } from "../Settings/EntityFormDialog";
import { InvoiceFormDialog } from "../Settings/InvoiceFormDialog";
import { hasAvatar, isLogoKind } from "./config";
import { useUploadLogo } from "../../api/logo";
import type {
  InvoiceEntity,
  NewCategoryEntity,
  NewEntity,
  NewExpenseTypeEntity,
  NewGroupEntity,
  NewInvoiceEntity,
  NewPayeeEntity,
  NewSourceEntity,
} from "../../api/types";
import { EntityAvatar } from "./EntityAvatar";
import { ENTITY_LABELS, type PickerKind } from "./config";
import type { PickerEntity } from "./entity";

type SingleProps<TId extends string> = {
  multiple?: false;
  value: TId | null;
  /**
   * The picked id, and the entity it names. The entity matters for one
   * created inline: the caller's registry snapshot predates it, so resolving
   * the id there (to a name, say) finds nothing until the next render.
   */
  onChange: (next: TId | null, entity: PickerEntity | null) => void;
  allowEmpty?: boolean;
};

type MultiProps<TId extends string> = {
  multiple: true;
  value: TId[];
  onChange: (next: TId[]) => void;
};

export type EntityPickerModalProps<TId extends string> = (
  | SingleProps<TId>
  | MultiProps<TId>
) & {
  open: boolean;
  onClose: () => void;
  kind: PickerKind;
  title?: string;
  includeHidden?: boolean;
  allowCreate?: boolean;
  items?: PickerEntity[];
  groupBy?: (item: PickerEntity) => string;
};

export const EntityPickerModal = <TId extends string>(
  props: EntityPickerModalProps<TId>,
) => {
  const { open, onClose, kind, title } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const effectiveTitle = title ?? `Select ${ENTITY_LABELS[kind]}`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen={isMobile}
      fullWidth
      maxWidth="xs"
      slotProps={{
        paper: {
          sx: { display: "flex", flexDirection: "column", maxHeight: "90vh" },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Box component="span">{effectiveTitle}</Box>
        <DialogCloseButton
          onClick={onClose}
          size="large"
          sx={{ minWidth: 44, minHeight: 44 }}
        />
      </DialogTitle>
      {open && <ModalBody {...props} />}
    </Dialog>
  );
};

const ModalBody = <TId extends string>(props: EntityPickerModalProps<TId>) => {
  const {
    onClose,
    kind,
    includeHidden = false,
    allowCreate = true,
    items,
    groupBy,
  } = props;
  const multiple = props.multiple === true;
  const showAvatar = hasAvatar(kind);

  const reg = useRegistry();
  const addSource = useAddSource();
  const addGroup = useAddGroup();
  const addCategory = useAddCategory();
  const addExpenseType = useAddExpenseType();
  const addPayee = useAddPayee();
  const addInvoice = useAddInvoice();
  const uploadLogo = useUploadLogo();

  const registryEntities = useMemo<PickerEntity[]>(() => {
    switch (kind) {
      case "source":
        return reg.sources as unknown as PickerEntity[];
      case "group":
        return reg.groups as unknown as PickerEntity[];
      case "category":
        return reg.categories as unknown as PickerEntity[];
      case "expense-type":
        return reg.expenseTypes as unknown as PickerEntity[];
      case "payee":
        return reg.payees as unknown as PickerEntity[];
      case "invoice":
        return reg.invoices as unknown as PickerEntity[];
      case "auto-transaction":
        // Rules don't live in the registry; the wrapper always supplies items.
        return [];
    }
  }, [kind, reg]);

  const sourceEntities = items ?? registryEntities;

  const visibleEntities = useMemo<PickerEntity[]>(
    () =>
      includeHidden ? sourceEntities : sourceEntities.filter((e) => !e.hidden),
    [sourceEntities, includeHidden],
  );

  const [search, setSearch] = useState("");
  const [stagedIds, setStagedIds] = useState<TId[]>(
    multiple ? (props as MultiProps<TId>).value : [],
  );
  const [pendingCreateName, setPendingCreateName] = useState<string | null>(
    null,
  );
  const [formError, setFormError] = useState<string | undefined>(undefined);
  // An entity that was created while its logo upload failed; set so the next
  // Save retries the upload instead of creating a duplicate.
  const [awaitingLogoFor, setAwaitingLogoFor] = useState<PickerEntity | null>(
    null,
  );

  const filteredOptions = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return visibleEntities;
    return visibleEntities.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.description?.toLowerCase().includes(q) ?? false),
    );
  }, [visibleEntities, search]);

  const exactMatch = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return false;
    return visibleEntities.some((e) => e.name.toLowerCase() === q);
  }, [visibleEntities, search]);

  const showCreateRow = allowCreate && !exactMatch;

  /** Close the create form and hand the new entity back to the caller. */
  const selectCreated = (created: PickerEntity) => {
    setAwaitingLogoFor(null);
    setPendingCreateName(null);
    setFormError(undefined);
    if (multiple) {
      setStagedIds((prev) => [...prev, created.id as TId]);
    } else {
      (props as SingleProps<TId>).onChange(created.id as TId, created);
      onClose();
    }
  };

  const handleCreateEntity = async (
    entity: NewEntity<string>,
    logoFile?: Blob | null,
  ) => {
    setFormError(undefined);
    try {
      // A previous attempt already created the entity and only the logo
      // failed — retry just the upload rather than POSTing a duplicate name.
      let created: PickerEntity | null = awaitingLogoFor;
      if (created === null) {
        switch (kind) {
          case "source":
            created = await addSource.mutateAsync(entity as NewSourceEntity);
            break;
          case "group":
            created = await addGroup.mutateAsync(entity as NewGroupEntity);
            break;
          case "category":
            created = await addCategory.mutateAsync(
              entity as NewCategoryEntity,
            );
            break;
          case "expense-type":
            created = await addExpenseType.mutateAsync(
              entity as NewExpenseTypeEntity,
            );
            break;
          case "payee":
            created = await addPayee.mutateAsync(entity as NewPayeeEntity);
            break;
          case "invoice":
            throw new Error("Invoices use InvoiceFormDialog");
          case "auto-transaction":
            throw new Error("Auto-transaction rules can't be created inline");
        }
      }

      // The logo is a separate blob upload, so it can only happen once the
      // entity has an id. Without this the picker silently drops the image the
      // user just cropped.
      if (logoFile && isLogoKind(kind)) {
        try {
          await uploadLogo.mutateAsync({
            kind,
            id: created.id,
            file: logoFile,
          });
        } catch (logoErr) {
          // The entity exists and is usable; only the image is missing. Stay
          // open so Save retries the upload alone.
          setAwaitingLogoFor(created);
          setFormError(
            `${ENTITY_LABELS[kind]} was created, but the logo upload failed${
              logoErr instanceof Error ? `: ${logoErr.message}` : ""
            }. Save to retry, or continue without it.`,
          );
          return;
        }
      }

      selectCreated(created);
    } catch (e) {
      setFormError(
        e instanceof Error
          ? e.message
          : `Failed to create ${ENTITY_LABELS[kind].toLowerCase()}`,
      );
    }
  };

  // Throws on failure: the invoice form shows the error itself, and turns a
  // duplicate refusal into its open / discard / create-anyway dialog.
  const handleCreateInvoice = async (
    entity: NewInvoiceEntity,
    { force }: { force: boolean },
  ) => {
    const created: InvoiceEntity = await addInvoice.mutateAsync({
      invoice: entity,
      force,
    });
    setPendingCreateName(null);
    (props as SingleProps<TId>).onChange(
      created.id as unknown as TId,
      created as unknown as PickerEntity,
    );
    onClose();
  };

  const handlePickSingle = (entity: PickerEntity) => {
    (props as SingleProps<TId>).onChange(entity.id as TId, entity);
    onClose();
  };

  const handleClearSingle = () => {
    (props as SingleProps<TId>).onChange(null, null);
    onClose();
  };

  const handleToggleMulti = (entity: PickerEntity) => {
    setStagedIds((prev) =>
      prev.includes(entity.id as TId)
        ? prev.filter((id) => id !== entity.id)
        : [...prev, entity.id as TId],
    );
  };

  const handleDoneMulti = () => {
    (props as MultiProps<TId>).onChange(stagedIds);
    onClose();
  };

  const isStaged = (entity: PickerEntity) =>
    multiple
      ? stagedIds.includes(entity.id as TId)
      : (props as SingleProps<TId>).value === entity.id;

  const allowEmpty = !multiple && (props as SingleProps<TId>).allowEmpty;
  const currentSingle =
    !multiple &&
    visibleEntities.find((e) => e.id === (props as SingleProps<TId>).value);

  return (
    <>
      <DialogContent
        dividers
        sx={{
          p: 0,
          display: "flex",
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box sx={{ p: 2, pb: 1 }}>
          <TextField
            autoFocus
            fullWidth
            size="small"
            placeholder={`Search ${ENTITY_LABELS[kind].toLowerCase()}…`}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            slotProps={{
              input: {
                startAdornment: (
                  <SearchIcon
                    fontSize="small"
                    sx={{ mr: 1, color: "text.secondary" }}
                  />
                ),
              },
            }}
          />
        </Box>
        <Box sx={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
          <List disablePadding>
            {showCreateRow && (
              <ListItemButton
                onClick={() => {
                  setFormError(undefined);
                  setPendingCreateName(search.trim());
                }}
                sx={{ minHeight: 48 }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: "primary.main",
                      color: "primary.contrastText",
                    }}
                  >
                    <AddIcon fontSize="small" />
                  </Avatar>
                </ListItemIcon>
                <ListItemText
                  primary={
                    search.trim().length > 0
                      ? `Create ${ENTITY_LABELS[kind].toLowerCase()} "${search.trim()}"`
                      : `Create new ${ENTITY_LABELS[kind].toLowerCase()}`
                  }
                />
              </ListItemButton>
            )}
            {!multiple && allowEmpty && (
              <ListItemButton
                selected={
                  currentSingle === undefined || currentSingle === false
                }
                onClick={handleClearSingle}
                sx={{ minHeight: 48 }}
              >
                {showAvatar && (
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    <Avatar
                      sx={{
                        width: 28,
                        height: 28,
                        bgcolor: "action.selected",
                        color: "text.secondary",
                      }}
                    />
                  </ListItemIcon>
                )}
                <ListItemText
                  primary={
                    <Typography
                      component="span"
                      sx={{ fontStyle: "italic", color: "text.secondary" }}
                    >
                      None
                    </Typography>
                  }
                />
              </ListItemButton>
            )}
            {renderItems(filteredOptions, groupBy, (entity, key) => (
              <ListItemButton
                key={key}
                selected={isStaged(entity)}
                onClick={() =>
                  multiple
                    ? handleToggleMulti(entity)
                    : handlePickSingle(entity)
                }
                sx={{ minHeight: 48 }}
              >
                {(multiple || showAvatar) && (
                  <ListItemIcon sx={{ minWidth: 40 }}>
                    {multiple ? (
                      <Checkbox
                        edge="start"
                        checked={isStaged(entity)}
                        tabIndex={-1}
                        disableRipple
                      />
                    ) : (
                      <EntityAvatar kind={kind} entity={entity} size={28} />
                    )}
                  </ListItemIcon>
                )}
                {multiple && showAvatar && (
                  <Box sx={{ mr: 1, display: "flex", alignItems: "center" }}>
                    <EntityAvatar kind={kind} entity={entity} size={24} />
                  </Box>
                )}
                <ListItemText
                  primary={entity.name}
                  secondary={entity.description ?? undefined}
                  slotProps={{
                    primary: {
                      sx: {
                        fontWeight:
                          entity.style === "bold" ? "bold" : undefined,
                        fontStyle:
                          entity.style === "italic" ? "italic" : undefined,
                      },
                    },
                  }}
                />
              </ListItemButton>
            ))}
            {filteredOptions.length === 0 && !showCreateRow && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography color="text.secondary" variant="body2">
                  No matches
                </Typography>
              </Box>
            )}
          </List>
        </Box>
      </DialogContent>
      {multiple && (
        <DialogActions sx={{ px: 2, py: 1.5 }}>
          <Stack
            direction="row"
            sx={{ width: "100%", alignItems: "center" }}
            spacing={1}
          >
            <Typography variant="body2" color="text.secondary">
              {stagedIds.length} selected
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="contained" onClick={handleDoneMulti}>
              Done
            </Button>
          </Stack>
        </DialogActions>
      )}
      {pendingCreateName !== null && kind === "invoice" && (
        <InvoiceFormDialog
          open
          title={`New ${ENTITY_LABELS[kind].toLowerCase()}`}
          onClose={() => setPendingCreateName(null)}
          onSave={handleCreateInvoice}
        />
      )}
      {pendingCreateName !== null &&
        kind !== "invoice" &&
        kind !== "auto-transaction" && (
          <EntityFormDialog
            open
            title={ENTITY_LABELS[kind]}
            entityType={kind}
            defaultName={pendingCreateName}
            error={formError}
            onClose={() => {
              // Giving up on a failed logo upload still leaves a usable
              // entity — select it rather than stranding it unselected.
              if (awaitingLogoFor) {
                selectCreated(awaitingLogoFor);
                return;
              }
              setPendingCreateName(null);
              setFormError(undefined);
            }}
            onSave={handleCreateEntity}
          />
        )}
    </>
  );
};

const renderItems = (
  items: PickerEntity[],
  groupBy: ((item: PickerEntity) => string) | undefined,
  renderItem: (item: PickerEntity, key: string) => React.ReactNode,
): React.ReactNode[] => {
  if (!groupBy) {
    return items.map((item) => renderItem(item, item.id));
  }
  const out: React.ReactNode[] = [];
  let lastGroup: string | null = null;
  for (const item of items) {
    const group = groupBy(item);
    if (group !== lastGroup) {
      out.push(
        <ListSubheader
          key={`group:${group}`}
          disableSticky
          sx={{ bgcolor: "background.paper", lineHeight: "32px" }}
        >
          {group}
        </ListSubheader>,
      );
      lastGroup = group;
    }
    out.push(renderItem(item, item.id));
  }
  return out;
};
